import { defineStore } from 'pinia';
import { AxiosRequestConfig } from "axios";
import handleTeacherError from "@/util/handle_teacher_error";
import apiRequest from '@/util/api-request';
import { ICbtUnitsData, ICbtClassroomStatuses, ICbtYearClassSelectOptions, ICbtSubjectFieldSelectOption, ICbtCorrectAnswers, ICbtAnswerResults, ITeacherError, ICbtScoreResults } from "@/consts/interfaces";

export const useCbtStore = defineStore('teacherCbtStore', {
  // actions
  actions: {
    async postCbtStart(payload) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: "/teacher/api/cbt/schedule/start/",
          data: payload
        };
        const CbtStartResult = await apiRequest(config);
        return CbtStartResult.data
      } catch (e) {
        handleTeacherError(e)
      }
    },
    async postCbtFinish(payload) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: "/teacher/api/cbt/schedule/finish/",
          data: payload
        };
        const CbtFinishResult = await apiRequest(config);
        return CbtFinishResult.data
      } catch (e) {
        handleTeacherError(e)
      }
    },
    async postCbtReturn(payload) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: "/teacher/api/cbt/schedule/return/",
          data: payload
        };
        const CbtReturnResult = await apiRequest(config);
        return CbtReturnResult.data
      } catch (e) {
        handleTeacherError(e)
      }
    },
    async fetchCbtYearClass(): Promise<ICbtYearClassSelectOptions | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/cbt/year_class/",
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        if (404 === e.response.status && "E005" === e.response.data.error.code) {
          // 404 (有効なCBT教材のライセンスがありません時) は、エラーモーダルではなく画面の本体にメッセージを表示する
          // FIXME NFR-6813: 型定義に調整が必要
          return {
            code: e.response.data.error.code,
            title: e.response.data.error.message,
            text: e.response.data.error.data?.detail
          } as ITeacherError;
        }
        // その他のエラー時は、handleTeacherErrorして共通エラーモーダルを表示する
        handleTeacherError(e);
      }
    },
    async fetchCbtSubjectFields(
      { academic_session_id, academic_year }:
      { academic_session_id: number; academic_year: number }
    ): Promise<ICbtSubjectFieldSelectOption[] | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/cbt/subject/list_present/`,
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year
          },
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data.subject_fields;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async fetchCbtUnits(
      { academic_session_id, academic_year, subject_field_id }:
      { academic_session_id: number; academic_year: number; subject_field_id: number }
    ): Promise<ICbtUnitsData | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/cbt/units/`,
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year,
            subject_field_id: subject_field_id
          },
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async fetchCbtClassroomStatuses(
      { academic_session_id, academic_year, subject_field_id }:
      { academic_session_id: number; academic_year: number; subject_field_id: number }
    ): Promise<ICbtClassroomStatuses | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/cbt/schedule/classroom_statuses/`,
          params: {
            academic_session_id: academic_session_id,
            academic_year: academic_year,
            subject_field_id: subject_field_id
          },
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async fetchCbtCorrectAnswers(
      { cbt_unit_id, classroom_uuid }:
      { cbt_unit_id: number; classroom_uuid: string }
    ): Promise<ICbtCorrectAnswers | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/cbt/correct_answers/`,
          params: {
            cbt_unit_id: cbt_unit_id,
            classroom_uuid: classroom_uuid
          },
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async fetchCbtAnswerResults(
      { cbt_unit_id, classroom_uuid }:
      { cbt_unit_id: number; classroom_uuid: string }
    ): Promise<ICbtAnswerResults | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/cbt/answer/results/`,
          params: {
            cbt_unit_id: cbt_unit_id,
            classroom_uuid: classroom_uuid
          },
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        if (400 === e.response.status && "cbt_execution_not_found" === e.response.data.error.data?.detail) {
          // 400 (学級にCBT実施がない場合) は、ブランクページを表示するため、エラーモーダルは表示しない
          // FIXME NFR-6813: 型定義に調整が必要
          return {
            code: e.response.data.error.code,
            title: e.response.data.error.message,
            text: e.response.data.error.data?.detail
          } as ITeacherError;
        }
        // その他のエラー時は、handleTeacherErrorして共通エラーモーダルを表示する
        handleTeacherError(e);
      }
    },
    async postCbtCorrectAnswerChange(payload: {
      cbt_quiz_id: number;
      academic_session_id: number;
      academic_year: number;
      answer: string;
    }) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: "/teacher/api/cbt/answer/correct_answer_change/",
          data: payload
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async postCbtCorrectAnswerChangeDelete(payload: {
      cbt_quiz_id: number;
      academic_session_id: number;
      academic_year: number;
      answer: string;
    }) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: "/teacher/api/cbt/answer/correct_answer_change/delete/",
          data: payload
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async fetchCbtScoreResults(cbt_unit_id: number, classroom_uuid: string): Promise<ICbtScoreResults | undefined> {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: `/teacher/api/cbt/score/results/`,
          data: {
            cbt_unit_id: cbt_unit_id,
            classroom_uuid: classroom_uuid
          },
        };
        const response = await apiRequest(config);
        return response.data;
      } catch (e) {
        handleTeacherError(e);
      }
    },
    async exportCbtScoreResults(
      { classroom_uuids, cbt_unit_ids }:
      { classroom_uuids: string[]; cbt_unit_ids: number[] }
    ): Promise<void> {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: `/teacher/api/cbt/export/`,
          data: {
            classroom_uuids: classroom_uuids,
            cbt_unit_ids: cbt_unit_ids
          },
          responseType: 'blob'
        };
        const response = await apiRequest(config);
        const contentDisposition = response.headers['content-disposition'];
        // fallback filename if content-disposition is not properly set
        let filename = 'CBT_Score_Results.xlsx';
        if (contentDisposition) {
          const utf8Match = contentDisposition.match(/filename\*=utf-8''([^'"]+)/i);
          if (utf8Match) {
            filename = decodeURIComponent(utf8Match[1]);
          }
        }

        // Handle the blob response here, e.g., download the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename); // You can customize the filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        handleTeacherError(e);
      }
    }
  }
})
